import React, { Component } from 'react';
import cn from 'classnames';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';

import { getUserFullName } from '../../utils/utilsChat';
import {
  isMobileSizeScreen,
  getHumanReadableFileSize,
  fitDimensionsIntoLimits,
  isWithinElement,
  isWebImage,
  isWebVideo,
} from '../../utils/utilsGlobal';

import { Avatar, LoadingSpinner, MAX_STICKER_SIZE } from '../../components';
import styles from './FullScreenPreviews.scss';


export class FullScreenPreviews extends Component {

  state = {
    isLoading: true,
    isLoadingError: false,
    currentPreview: {},
    currentPreviewType: null,
    currentPreviewWidth: null,
    currentPreviewHeight: null,
    currentPreviewTopMargin: null,
  };

  HORIZONTAL_PADDINGS = isMobileSizeScreen() ? 20 : 100;
  VERTICAL_PADDINGS = 140;
  video = React.createRef();

  componentDidMount(){
    document.body.addEventListener('keyup', this.onBodyKeyup);
    document.body.classList.add(styles.bodyBackground);
  }

  componentWillUnmount(){
    document.body.removeEventListener('keyup', this.onBodyKeyup);
    document.body.classList.remove(styles.bodyBackground);
  }

  openPreview = (attachmentId) => {
    const { previews, onToggle } = this.props;
    const currentPreview = find(previews, { id: attachmentId }) || null;

    this.onPreviewChange(currentPreview, () => {
      onToggle && onToggle( Boolean(currentPreview) );
    });
  };

  onPreviewChange = (currentPreview, callback) => {
    const currentPreviewType = this.detectPreviewType(currentPreview);
    if (currentPreview && currentPreviewType) {
      this.setState({
        currentPreview,
        currentPreviewType,
        currentPreviewWidth: null,
        currentPreviewHeight: null,
        currentPreviewTopMargin: null,
        isLoading: true,
      });
      if (currentPreviewType === 'image') {
        this.loadImage(currentPreview);
        callback && callback();
      }
      else if (currentPreviewType === 'video') {
        this.loadVideo(currentPreview);
        callback && callback();
      }
    }
  };

  detectPreviewType = (preview) => {
    if (!preview) {
      return null;
    }
    const { contentType, thumbnails } = preview;
    const thumbnailUrl = thumbnails?.[0]?.url || '';

    if (isWebImage(contentType) && thumbnailUrl) {
      return 'image';
    }
    else if (isWebVideo(contentType) && thumbnailUrl) {
      return 'video';
    }
    return null;
  };

  closePreview = () => {
    const { onToggle } = this.props;
    this.setState({
      currentPreview: null,
      currentPreviewType: null,
      currentPreviewWidth: null,
      currentPreviewHeight: null,
      currentPreviewTopMargin: null,
    });
    onToggle && onToggle(false);
  };

  calculatePreviewTopMargin = (currentPreviewHeight) => {
    const availableVerticalSpace = window.innerHeight - this.VERTICAL_PADDINGS;
    if (availableVerticalSpace < currentPreviewHeight) {
      return 0;
    }
    else {
      return (availableVerticalSpace - currentPreviewHeight) / 2;
    }
  };

  loadImage = (preview) => {
    const { url, width, height, isSticker } = preview;
    const image = new Image();
    const maxPreviewWidth = window.innerWidth - this.HORIZONTAL_PADDINGS;
    const maxWidth = isSticker ? MAX_STICKER_SIZE : maxPreviewWidth;
    image.onload = () => {
      const [ currentPreviewWidth, currentPreviewHeight ] = fitDimensionsIntoLimits(width, height, maxWidth, null);
      const currentPreviewTopMargin = this.calculatePreviewTopMargin(currentPreviewHeight);
      this.setState({
        isLoading: false,
        currentPreviewWidth,
        currentPreviewHeight,
        currentPreviewTopMargin,
      });
    };
    image.onerror = () => {
      this.setState({
        isLoading: false,
        isLoadingError: true,
      });
    };
    image.src = url;
  };

  loadVideo = (preview) => {
    const { width, height } = preview;
    const maxPreviewWidth = window.innerWidth - this.HORIZONTAL_PADDINGS;
    const maxPreviewHeight = window.innerHeight - this.VERTICAL_PADDINGS;
    const [ currentPreviewWidth, currentPreviewHeight ] = fitDimensionsIntoLimits(width, height, maxPreviewWidth, maxPreviewHeight);
    const currentPreviewTopMargin = this.calculatePreviewTopMargin(currentPreviewHeight);
    this.setState({
      isLoading: false,
      currentPreviewWidth,
      currentPreviewHeight,
      currentPreviewTopMargin,
    }, () => {
      this.video.current.focus();
    });
  };

  onBodyKeyup = (e) => {
    if (e.key === 'Escape') {
      this.closePreview();
    }
    else if (e.key === 'ArrowLeft') {
      this.onArrowNavigation(-1);
    }
    else if (e.key === 'ArrowRight') {
      this.onArrowNavigation(1);
    }
  };

  onArrowNavigation = (delta) => {
    const { previews } = this.props;
    const { currentPreview } = this.state;
    const currentPreviewIndex = findIndex(previews, { id: currentPreview?.id });

    if (currentPreviewIndex >= 0) {
      let nextPreviewIndex = currentPreviewIndex + delta;
      if (nextPreviewIndex < 0) {
        nextPreviewIndex = previews.length - 1;
      }
      else if (nextPreviewIndex >= previews.length) {
        nextPreviewIndex = 0;
      }
      this.onPreviewChange(previews[nextPreviewIndex]);
    }
  };

  onContainerClick = (e) => {
    if (!isWithinElement(e.target, styles.containerInner)) {
      this.closePreview();
    }
  };

  render() {
    const { className } = this.props;
    const {
      isLoading,
      isLoadingError,
      currentPreview,
      currentPreviewType,
      currentPreviewWidth,
      currentPreviewHeight,
      currentPreviewTopMargin,
    } = this.state;

    if (!currentPreview?.id) {
      return null;
    }

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerLoading]: isLoading,
        [styles.containerLoadingError]: isLoadingError,
      })} onClick={this.onContainerClick}>

        <div className={styles.containerInner} style={{ marginTop: currentPreviewTopMargin }}>
          {currentPreviewType === 'image' && !isLoadingError && !isLoading && (
            <img className={cn({
              [styles.image]: true,
              [styles.imageSticker]: currentPreview.isSticker,
            })}
              width={currentPreviewWidth}
              height={currentPreviewHeight}
              src={currentPreview.url}
              alt={currentPreview.name}
              onClick={this.closePreview}
            />
          )}

          {currentPreviewType === 'video' && !isLoadingError && !isLoading && (
            <video className={styles.video}
              ref={this.video}
              controls={true}
              autoPlay={true}
              width={currentPreviewWidth}
              height={currentPreviewHeight}
              src={currentPreview.url}/>
          )}

          {isLoading && (
            <LoadingSpinner className={styles.spinner} size="lg"/>
          )}

          {isLoadingError && (
            <span className={styles.fileNotFound}>
              Файл не найден
              <div className={styles.fileNotFoundName}>{currentPreview.name}</div>
            </span>
          )}

          <div className={styles.caption}>
            <Avatar user={currentPreview.sender}/>
            <div className={styles.captionText}>
              <b>{getUserFullName(currentPreview.sender)}</b><br/>
              {currentPreviewType === 'image' && currentPreview.name}
              {currentPreviewType === 'video' && (
                <a className={styles.captionLink} href={currentPreview.url} target="_blank" download={true}>
                  {currentPreview.name} ({getHumanReadableFileSize(currentPreview.bytesSize)})
                </a>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }
}
